import React, { useState } from 'react'
import { TextField, Grid, IconButton, Slider, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import useEmailEditorStore from '../../utilities/emailEditorStore';
import BlockProps from './common/BlockProps';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormatAlignCenter, FormatAlignLeft, FormatAlignRight, RoundedCorner, PhotoLibrary } from '@mui/icons-material';
import AdminResourceImagePicker from '../../../../articles/AdminResourceImagePicker';

function ImageBlockProperties({ blockId }) {
  const { getBlockById, updateBlockProperties } = useEmailEditorStore();
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const block = getBlockById(blockId);

  if (!block) {
    return <Typography>Block not found</Typography>;
  }

  const properties = block.properties || {};

  const handlePropertyChange = (propertyName, value) => {
    updateBlockProperties(blockId, { [propertyName]: value });
  };

  const handleMediaSelect = (imagePath, item) => {
    updateBlockProperties(blockId, {
      src: imagePath,
      ...(item?.name ? { alt: item.name } : {}),
    });
  };

  return (
    <>
      <AdminResourceImagePicker
        show={showMediaPicker}
        onHide={() => setShowMediaPicker(false)}
        onSelect={handleMediaSelect}
      />

      <TextField
        fullWidth
        label="Image URL"
        value={properties.src || ''}
        onChange={(e) => handlePropertyChange('src', e.target.value)}
        sx={{ mb: 1 }}
      />

      <Button
        fullWidth
        variant="outlined"
        startIcon={<PhotoLibrary />}
        onClick={() => setShowMediaPicker(true)}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Browse 3ConX Media Library
      </Button>

      {properties.src ? (
        <Box
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            maxHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, .03)',
          }}
        >
          <Box
            component="img"
            src={properties.src}
            alt={properties.alt || 'Selected image'}
            sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'block' }}
          />
        </Box>
      ) : null}

      <TextField
        fullWidth
        label="Alt Text"
        value={properties.alt || ''}
        onChange={(e) => handlePropertyChange('alt', e.target.value)}
        sx={{ mb: 2 }}
      />
      <Box>
        <Accordion sx={{ mb: 2, backgroundColor: 'rgba(0, 0, 0, .03)' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Image Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Width"
                    value={properties.width || '100%'}
                    onChange={(e) => handlePropertyChange('width', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Height"
                    value={properties.height || 'auto'}
                    onChange={(e) => handlePropertyChange('height', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Alignment
              </Typography>
              <Box>
                <IconButton
                  onClick={() => handlePropertyChange('textAlign', 'left')}
                  color={properties.textAlign === 'left' ? 'primary' : 'default'}
                >
                  <FormatAlignLeft />
                </IconButton>
                <IconButton
                  onClick={() => handlePropertyChange('textAlign', 'center')}
                  color={properties.textAlign === 'center' ? 'primary' : 'default'}
                >
                  <FormatAlignCenter />
                </IconButton>
                <IconButton
                  onClick={() => handlePropertyChange('textAlign', 'right')}
                  color={properties.textAlign === 'right' ? 'primary' : 'default'}
                >
                  <FormatAlignRight />
                </IconButton>
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Border Radius
              </Typography>
              <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                <Grid>
                  <RoundedCorner />
                </Grid>
                <Grid size="grow">
                  <Slider
                    value={properties.imgborderRadius}
                    onChange={(e) => handlePropertyChange('imgborderRadius', e.target.value)}
                    aria-labelledby="input-slider-borderradius"
                    step={1}
                    marks
                    max={600}
                  />
                </Grid>
                <Grid>
                  <Typography variant="body2" gutterBottom>
                    {properties.imgborderRadius} px
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
        <BlockProps properties={properties} onPropertyChange={handlePropertyChange} />
      </Box>
    </>
  )
}

export default ImageBlockProperties
